'use client';

import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

const domains = [
  {
    title: 'Financial Planning',
    desc: 'Revenue planning, expense planning, headcount planning, cash forecasting, balance sheet planning.',
    useCases: 12,
    color: '#F0F4FF',
  },
  {
    title: 'Sales Planning',
    desc: 'Sales quotas, pipeline forecasting, territory planning, sales incentives, demand forecasting.',
    useCases: 10,
    color: '#F0FFF4',
  },
  {
    title: 'Supply Chain Planning',
    desc: 'Demand planning, inventory planning, procurement forecasting, production planning, capacity planning.',
    useCases: 11,
    color: '#FFFBF0',
  },
  {
    title: 'Enterprise Planning',
    desc: 'Integrated business planning, strategic planning, scenario simulation, rolling forecasts, executive dashboards.',
    useCases: 17,
    color: '#FFF0F0',
  },
];

export default function PlanningDomains() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-on-scroll').forEach((el) => {
              (el as HTMLElement).classList.add('is-visible');
            });
          }
        });
      },
      { threshold: 0.05 }
    );
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 px-6 border-b border-border bg-secondary">
      <div className="max-w-container mx-auto">
        <div className="mb-12 reveal-on-scroll">
          <div className="mono-label mb-3">Planning Coverage</div>
          <h2
            className="font-heading font-semibold text-foreground"
            style={{ fontSize: 'clamp(1.75rem, 3vw, 2.75rem)', letterSpacing: '-0.025em' }}
          >
            Enterprise Planning Domains
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {domains.map((domain, i) => (
            <div
              key={domain.title}
              className={`domain-card reveal-on-scroll reveal-delay-${i + 1} bg-white border border-border p-8 cursor-default`}
              style={{ borderRadius: '4px' }}
            >
              <div className="flex items-start justify-between mb-4">
                <h3 className="font-heading font-semibold text-foreground text-lg">
                  {domain.title}
                </h3>
                <span className="mono-label ml-4 shrink-0">
                  {domain.useCases} use cases
                </span>
              </div>
              <p className="text-sm text-muted leading-relaxed font-body">
                {domain.desc}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 reveal-on-scroll reveal-delay-5">
          <Link href="/use-cases" className="btn-outline text-sm">
            View All 50 Use Cases →
          </Link>
        </div>
      </div>
    </section>
  );
}